import React, { useEffect, useState } from 'react';
import TableComponent from 'components/table-component/Table';
import Error from 'components/Error';
import { useTranslation } from 'react-i18next';
import { topicsSetsHeaders } from '../utils';
import AlertModal from 'components/common-components/AlertModal';
import toast from 'react-hot-toast';
import { CallAPI } from 'actions/General';
import { isPermissionAvailable } from 'config/permissionUtils';
import { permissionKeys } from 'config/config';
import FormButton from 'components/form-components/FormButton';
import plusIcon from 'assets/images/icons/plus.svg';
import TopicsSetsListHeaders from './TopicsSetsListHeaders';
import AddOptionsSetsModal from './AddOptionsSetsModal';
import ClientPagination from '../ClientPagination';

const TopicsSets = () => {
  const { t } = useTranslation();

  const [topicSets, setTopicSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [inputFilters, setInputFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [APIError, setAPIError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [paginatedData, setPaginatedData] = useState([]);

  const handleFilterChange = (name, value) => {
    setInputFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDebouncedSearch = (name, value) => {
    setAppliedFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddTopicSet = () => {
    setInputFilters({});
    setShowAddModal(true);
  };

  const handleCloseEditModal = () => {
    setSelectedRecordId(null);
    setShowAddModal(false);
  };

  const handleDeleteRecord = async () => {
    const result = await CallAPI(
      'DELETE_TOPICS_SET',
      null,
      selectedRecordId,
      setDeleteLoading,
      setAPIError,
    );
    if (result.status) {
      toast.success(t('alert_success_topic_set_deleted'));
      fetchTopicSets();
    }
    setShowDeleteConfirm(false);
  };

  const editDeleteTopicSetClick = (id, action) => {
    if (action === 'edit') {
      setSelectedRecordId(id);
      setShowAddModal(true);
    } else if (action === 'delete') {
      setSelectedRecordId(id);
      setShowDeleteConfirm(true);
    }
  };

  const fetchTopicSets = async () => {
    setSelectedRecordId(null);
    const result = await CallAPI(
      'GET_ALL_TOPICS_SETS',
      { searchText: appliedFilters?.search ?? '' },
      null,
      setLoading,
      setAPIError,
    );
    if (result.status) {
      result.data
        .sort((a, b) =>
          a.title.localeCompare(b.title, ['ar', 'en'], {
            sensitivity: 'base',
          }),
        )
        .forEach((value, index) => {
          value.indexed = index + 1;
        });
      setTopicSets(result.data);
    }
  };

  useEffect(() => {
    fetchTopicSets();
  }, [appliedFilters]);

  return (
    <div>
      {APIError && <Error msg={APIError} />}

      <TopicsSetsListHeaders
        filteringData={inputFilters}
        setFilteringData={handleFilterChange}
        debounceFunc={handleDebouncedSearch}
        handleAddTopicSet={handleAddTopicSet}
      />

      <TableComponent
        header={topicsSetsHeaders}
        data={paginatedData}
        props={{
          editDeleteTopicSetClick: editDeleteTopicSetClick,
        }}
        totalRecords={0}
        loader={loading}
        filterEnabled={false}
        isLoading={loading}
        isFieldSelectionEnabled={false}
        isPaginationEnabled={false}
        isActionsEnabled={false}
        noDataComponent={
          <div className='lh-1 w-100 d-flex flex-column gap-2 justify-content-center align-items-center'>
            <span className='theme-text-black-1 theme-size-1_3'>
              {t('text_you_dont_have_any_topics')}
            </span>
            {isPermissionAvailable(permissionKeys.field_work_response_set_create) && (
              <>
                <span>{t('text_add_to_get_started')}</span>
                <FormButton
                  text='button_add_new'
                  variant='green-1'
                  onClick={handleAddTopicSet}
                  icon={plusIcon}
                  iconClasses='svg-white'
                />
              </>
            )}
          </div>
        }
      />

      {topicSets.length > 0 && (
        <div className='p-3'>
          <ClientPagination
            data={topicSets}
            onChange={({ paginatedDataList }) => {
              setPaginatedData(paginatedDataList);
            }}
          />
        </div>
      )}

      {showAddModal && (
        <AddOptionsSetsModal
          show={showAddModal}
          editId={selectedRecordId}
          onCancel={handleCloseEditModal}
          refreshList={fetchTopicSets}
        />
      )}

      <AlertModal
        show={showDeleteConfirm}
        showCloseButton={true}
        closeModal={() => setShowDeleteConfirm(false)}
        variant='delete'
        message={'alert_delete_topic_set'}
        actionButtons={[
          {
            text: t('button_confirm'),
            variant: 'red-1',
            onClick: () => {
              handleDeleteRecord();
            },
            loading: deleteLoading,
          },
          {
            text: t('button_cancel'),
            variant: 'white-1',
            onClick: () => {
              setShowDeleteConfirm(false);
              setSelectedRecordId(null);
            },
          },
        ]}
      />
    </div>
  );
};

export default TopicsSets;
